import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ToggleSwitch2Component } from '@frontendlib/components/controls/toggle-switch/toggle-switch2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { TooltipDirective } from '@modules/tooltip/tooltip.directive';
import { KnotsModule } from '@modules/knots/knots.module';
import { KnotsService } from '@modules/knots/services/knots.service';
import { Knot } from '@modules/knots/types/knot';
import { KnotFilters } from '@modules/knots/types/knot-filters';
import { Message } from '@modules/messages/types/message';
import { applyInstanceDecorators, DeferInstanceDecorator } from '@sharedlib/common/lang/DeferInstanceDecorator';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { firstValueFrom, merge, of, Subject, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { LocalStorageItem, LSNumber } from '@frontendlib/common/decorators/local-storage.decorator';

@Component({
  selector: 'app-outbound-knots-control',
  standalone: true,
  templateUrl: './outbound-knots-control.component.html',
  styleUrls: ['./outbound-knots-control.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PopoverDirective,
    FormControlsModule,
    KnotsModule,
    Button2Component,
    IconComponent,
    ToggleSwitch2Component,
    TooltipDirective,
  ],
})
export class OutboundKnotsControlComponent implements OnDestroy {
  // Inputs
  public readonly inputFormControl = input.required<FormControl<boolean>>();
  public readonly messageId = input<string>();

  // Outputs
  public readonly previewRequested = output<void>();

  // Signals
  protected readonly processing = signal<boolean>(false);
  protected readonly knots = signal<Knot[]>([]);
  protected readonly previewTimeout = signal(0);
  protected readonly isPreviewDisabled = computed(() => this.processing() || this.previewTimeout() > 0);
  protected readonly previewBtnLabel = computed(() => {
    const label = 'Preview';
    return this.previewTimeout() ? `${label} ${this.previewTimeout()}s` : label;
  });
  protected readonly previewKnotsArrived = signal<boolean>(false);
  protected readonly isKnotsEnabled = signal<boolean>(false);
  protected readonly shouldShowDisabledState = computed(() => {
    return !this.isKnotsEnabled();
  });
  protected readonly shouldShowKnotsProcessingState = computed(() => {
    return this.processing() && this.isKnotsEnabled();
  });
  protected readonly shouldShowEnabledNoKnotsState = computed(() => {
    return !this.processing() && !this.knots().length && !this.previewKnotsArrived() && this.isKnotsEnabled();
  });
  protected readonly shouldShowNoValidKnots = computed(() => {
    return !this.processing() && !this.knots().length && this.previewKnotsArrived() && this.isKnotsEnabled();
  });
  protected readonly shouldShowKnots = computed(() => {
    return !this.processing() && this.knots().length && this.isKnotsEnabled();
  });
  protected readonly shouldShowTextState = computed(() => {
    return (
      this.shouldShowDisabledState() ||
      this.shouldShowEnabledNoKnotsState() ||
      this.shouldShowNoValidKnots() ||
      this.processing()
    );
  });

  // Local Storage
  @DeferInstanceDecorator(LSNumber({ lsKey: 'outbound-knots-control__preview-cooldown', default: 0 }))
  private readonly localStoragePreviewCooldown: LocalStorageItem<number>;

  // Injects
  private readonly knotsService = inject(KnotsService);
  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly spaceContextStore = inject(SpaceContextStore);

  // Local vars
  private readonly previewButtonTimeoutInSec = 30;
  private readonly alive = new Subject<void>();
  private processKnotsInterval: ReturnType<typeof setInterval> | null = null;
  private readonly FALLBACK_POLL_DELAY_MS = 15000;
  private readonly MAX_FALLBACK_POLL_ATTEMPTS = 15000;
  private readonly STOP_CHECK_INTERVAL_MS = 300;
  private fallbackPoolingStop$: Subject<void>;
  private fallbackPoolingInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    applyInstanceDecorators(this, OutboundKnotsControlComponent);
    // Fetch knots when the component is initialized and has messageId set
    toObservable(this.messageId)
      .pipe(
        distinctUntilChanged(),
        filter((messageId) => !!messageId),
        filter(() => !this.processing()),
        switchMap((messageId) => this.knotsService.search(this.getKnotFilters(messageId))),
        takeUntil(this.alive),
      )
      .subscribe({
        next: async (resp) => {
          this.knots.set(resp.items);
          await this.restartPreviewTimeoutIfNecessary(this.messageId());
        },
        complete: () => this.processing.set(false),
        error: () => this.processing.set(false),
      });

    // Fetch knots and update the control state once we receive the "knots-updated" signal
    toObservable(this.messageId)
      .pipe(
        distinctUntilChanged(),
        filter((messageId) => !!messageId),
        switchMap((messageId) => {
          return this.stitchEventsService
            .getEvents(this.spaceContextStore.contextId(), StitchType.MESSAGE, SignalStitchEventType.KNOTS_UPDATED)
            .pipe(
              filter((event) => event.ids.includes(messageId)),
              map(() => messageId),
            );
        }),
        switchMap((messageId) => {
          this.processing.set(true);
          return this.knotsService.search(this.getKnotFilters(messageId));
        }),
        takeUntil(this.alive),
      )
      .subscribe((knotsResp) => {
        this.knots.set(knotsResp.items);
        this.processing.set(false);
        this.previewKnotsArrived.set(true);
        this.startPreviewButtonTimeout();
      });

    // When outbound knots are enabled,
    // try to fetch existing knots from the backend if any
    toObservable(this.inputFormControl)
      .pipe(
        switchMap((formControl) => formControl.valueChanges.pipe(startWith(formControl.value))),
        filter((value) => value && !!this.messageId()),
        switchMap(() => this.knotsService.search(this.getKnotFilters(this.messageId()))),
        takeUntil(this.alive),
      )
      .subscribe((knotsResp) => {
        this.knots.set(knotsResp.items);
      });

    toObservable(this.inputFormControl)
      .pipe(
        switchMap((formControl) => formControl.valueChanges.pipe(startWith(formControl.value))),
        takeUntil(this.alive),
      )
      .subscribe((value) => {
        this.isKnotsEnabled.set(value);
      });
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
    if (this.processKnotsInterval) {
      clearInterval(this.processKnotsInterval);
    }
    if (this.fallbackPoolingStop$ !== undefined && !this.fallbackPoolingStop$.closed) {
      this.fallbackPoolingStop$.next();
      this.fallbackPoolingStop$.complete();
    }
  }

  protected unLinkKnot(knot: Knot): void {
    // error is handled internally by .unlink with the toast message
    this.knotsService.unlink([knot], [new Message({ id: this.messageId() })]).subscribe(() => {
      this.knots.set(this.knots().filter((k) => k.id !== knot.id));
    });
  }

  protected onPreviewClick(): void {
    if (this.processing() || this.previewTimeout()) return;
    this.processing.set(true);
    this.previewRequested.emit();
    this.startFallbackPolling();
  }

  /**
   * Initiates a fallback polling for knots via HTTP
   * Quite often websocket connection drops and does not correctly re-connects
   * or when we do account switching, sometimes websocket stops working
   * This is an HTTP fallback for such a case.
   * We execute it after 15 seconds without websocket response.
   *
   * This leads to the following outcome. If knot extraction via bridge takes a long time.
   * We'll get manual knots first, via this method. Then the Preview button timeout will start.
   * If we ever get a websocket event from the backend, we will update knots on the screen.
   *
   * @return {void} This method does not return any value.
   */
  private startFallbackPolling(): void {
    if (this.fallbackPoolingStop$ !== undefined && !this.fallbackPoolingStop$.closed) {
      // this also clears out an interval
      this.fallbackPoolingStop$.next();
      this.fallbackPoolingStop$.complete();
    }
    this.fallbackPoolingStop$ = new Subject<void>();

    // Stop when knots arrived via WS or a poll, processing flips false, control disabled, or messageId changes
    this.fallbackPoolingInterval = setInterval(() => {
      if (!this.processing() || this.knots().length > 0 || !this.isKnotsEnabled()) {
        this.fallbackPoolingStop$.next();
      }
    }, this.STOP_CHECK_INTERVAL_MS);

    let attempts = 0;

    merge(of(0), timer(this.FALLBACK_POLL_DELAY_MS, this.FALLBACK_POLL_DELAY_MS))
      .pipe(takeUntil(this.fallbackPoolingStop$), takeUntil(this.alive))
      .subscribe(async () => {
        // Re-check stop conditions before each attempt
        if (!this.processing() || this.knots().length > 0 || !this.isKnotsEnabled()) {
          this.fallbackPoolingStop$.next();
          return;
        }
        if (!this.messageId()) {
          return;
        }

        attempts += 1;
        try {
          const resp = await firstValueFrom(this.knotsService.search(this.getKnotFilters(this.messageId())));
          const items = resp.items ?? [];
          if (items.length > 0) {
            this.knots.set(items);
            this.processing.set(false);
            this.previewKnotsArrived.set(true);
            this.startPreviewButtonTimeout();
            this.fallbackPoolingStop$.next();
          } else if (attempts >= this.MAX_FALLBACK_POLL_ATTEMPTS) {
            this.processing.set(false);
            this.previewKnotsArrived.set(true); // show "No valid knots"
            this.startPreviewButtonTimeout();
            this.fallbackPoolingStop$.next();
          }
        } catch {
          if (attempts >= this.MAX_FALLBACK_POLL_ATTEMPTS) {
            this.processing.set(false);
            this.previewKnotsArrived.set(true);
            this.startPreviewButtonTimeout();
            this.fallbackPoolingStop$.next();
          }
        }
      });

    // finalize local resources when stop$ fires or component destroyed
    this.fallbackPoolingStop$.pipe(takeUntil(this.alive)).subscribe(() => {
      clearInterval(this.fallbackPoolingInterval);
      this.fallbackPoolingStop$.complete();
    });
  }

  private startPreviewButtonTimeout(): void {
    if (this.processKnotsInterval) {
      clearInterval(this.processKnotsInterval);
    }
    this.localStoragePreviewCooldown.set(Date.now() + this.previewButtonTimeoutInSec * 1000, this.messageId());
    this.executePreviewButtonTimeout(this.previewButtonTimeoutInSec);
  }

  private async restartPreviewTimeoutIfNecessary(messageId: string): Promise<void> {
    const cooldownTillMs = await firstValueFrom(this.localStoragePreviewCooldown.get(messageId));
    if (cooldownTillMs === 0 || Date.now() >= cooldownTillMs) {
      return;
    }
    const durationInSec = Math.max(1, Math.round((cooldownTillMs - Date.now()) / 1000));
    this.executePreviewButtonTimeout(durationInSec);
  }

  private executePreviewButtonTimeout(timeoutInSec: number): void {
    this.previewTimeout.set(timeoutInSec);
    this.processKnotsInterval = setInterval(() => {
      this.previewTimeout.update((prev) => prev - 1);
      if (this.previewTimeout() === 0) {
        clearInterval(this.processKnotsInterval);
        this.processKnotsInterval = null;
        this.localStoragePreviewCooldown.set(0, this.messageId());
      }
    }, 1000);
  }

  private getKnotFilters(messageId: string): KnotFilters {
    return new KnotFilters({
      items: [new Message({ id: messageId })],
      sortBy: 'name',
      limit: 50,
    });
  }
}
